import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, message, Popconfirm, Select, Modal, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined, TrophyOutlined } from '@ant-design/icons';
import { TournamentInvitationService } from '@/services/tournamentInvitation.service';
import { ClubService } from '@/services/club.service';
import type { TournamentInvitation } from '@/types/api.types';
import type { Club } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { formatDate } from '@/utils/date';

const { Option } = Select;

interface TournamentInvitationsManagementProps {
  tournamentId: number;
  isInviteOnly?: boolean;
  onInvitationsChange?: () => void;
}

export const TournamentInvitationsManagement: React.FC<TournamentInvitationsManagementProps> = ({
  tournamentId,
  isInviteOnly = false,
  onInvitationsChange
}) => {
  const [invitations, setInvitations] = useState<TournamentInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [selectedClubIds, setSelectedClubIds] = useState<number[]>([]);
  const [inviting, setInviting] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);

  useEffect(() => {
    fetchInvitations();
  }, [tournamentId]);

  useEffect(() => {
    if (isInviteOnly) {
      fetchClubs();
    }
  }, [isInviteOnly, invitations.length]); // Обновляем список клубов при изменении приглашений

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const data = await TournamentInvitationService.getTournamentInvitations(tournamentId);
      setInvitations(data);
    } catch (error) {
      logger.error('Error fetching invitations:', error);
      message.error('Не удалось загрузить список приглашений');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      setLoadingClubs(true);
      const response = await ClubService.getClubs({ limit: 1000 });
      const clubsList = Array.isArray(response.data) ? response.data : response.data.rows || [];
      // Исключаем уже приглашенные клубы из списка доступных
      const invitedClubIds = invitations.map(inv => inv.clubId);
      const availableClubs = clubsList.filter(club => !invitedClubIds.includes(club.id));
      setClubs(availableClubs);
    } catch (error) {
      logger.error('Error fetching clubs:', error);
      message.warning('Не удалось загрузить список клубов');
    } finally {
      setLoadingClubs(false);
    }
  };

  const handleInvite = async () => {
    if (selectedClubIds.length === 0) {
      message.warning('Выберите хотя бы один клуб для приглашения');
      return;
    }

    try {
      setInviting(true);
      await TournamentInvitationService.inviteClubs(tournamentId, selectedClubIds);
      message.success(`Приглашения отправлены ${selectedClubIds.length} клубам`);
      setSelectedClubIds([]);
      setInviteModalVisible(false);
      await fetchInvitations();
      // fetchClubs будет вызван через useEffect при изменении invitations
      onInvitationsChange?.();
    } catch (error: any) {
      logger.error('Error inviting clubs:', error);
      message.error(error?.response?.data?.message || 'Не удалось отправить приглашения');
    } finally {
      setInviting(false);
    }
  };

  const handleDelete = async (invitationId: number) => {
    try {
      await TournamentInvitationService.deleteInvitation(invitationId);
      message.success('Приглашение удалено');
      await fetchInvitations();
      // fetchClubs будет вызван через useEffect при изменении invitations
      onInvitationsChange?.();
    } catch (error: any) {
      logger.error('Error deleting invitation:', error);
      message.error(error?.response?.data?.message || 'Не удалось удалить приглашение');
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      pending: { color: 'orange', text: 'Ожидает ответа' },
      accepted: { color: 'green', text: 'Принято' },
      rejected: { color: 'red', text: 'Отклонено' }
    };

    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'Клуб',
      dataIndex: 'club',
      key: 'club',
      render: (club: any) => (
        <Space>
          <UserOutlined />
          <span>{club?.name || 'Неизвестный клуб'}</span>
          {club?.city && <span style={{ color: '#8c8c8c' }}>({club.city})</span>}
        </Space>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status)
    },
    {
      title: 'Приглашено',
      dataIndex: 'invitedAt',
      key: 'invitedAt',
      render: (date: string) => formatDate(date)
    },
    {
      title: 'Ответ получен',
      dataIndex: 'respondedAt',
      key: 'respondedAt',
      render: (date: string | null) => date ? formatDate(date) : '-'
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: TournamentInvitation) => (
        <Popconfirm
          title="Удалить приглашение?"
          description="Это действие нельзя отменить"
          onConfirm={() => handleDelete(record.id)}
          okText="Да"
          cancelText="Нет"
        >
          <Button danger size="small" icon={<DeleteOutlined />}>
            Удалить
          </Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <Card
      title={
        <Space>
          <TrophyOutlined />
          <span>Приглашения на турнир</span>
        </Space>
      }
      extra={
        isInviteOnly && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setInviteModalVisible(true)}
          >
            Пригласить клубы
          </Button>
        )
      }
    >
      <Table
        columns={columns}
        dataSource={invitations}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Всего: ${total}`
        }}
        locale={{
          emptyText: (
            <Empty
              description="Нет приглашений"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
      />

      <Modal
        title="Пригласить клубы на турнир"
        open={inviteModalVisible}
        onOk={handleInvite}
        onCancel={() => {
          setInviteModalVisible(false);
          setSelectedClubIds([]);
        }}
        confirmLoading={inviting}
        okText="Отправить приглашения"
        cancelText="Отмена"
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Выберите клубы, которые будут приглашены на турнир:</p>
          <Select
            mode="multiple"
            placeholder="Выберите клубы"
            style={{ width: '100%' }}
            loading={loadingClubs}
            value={selectedClubIds}
            onChange={setSelectedClubIds}
            optionFilterProp="children"
            showSearch
            filterOption={(input, option) => {
              const label = typeof option?.children === 'string' 
                ? option.children 
                : String(option?.children || '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {clubs.map(club => (
              <Option key={club.id} value={club.id}>
                {club.name} {club.city ? `(${club.city})` : ''}
              </Option>
            ))}
          </Select>
        </div>
        {selectedClubIds.length > 0 && (
          <p style={{ color: '#8c8c8c', fontSize: '12px' }}>
            Будет отправлено приглашений: {selectedClubIds.length}
          </p>
        )}
      </Modal>
    </Card>
  );
};

export default TournamentInvitationsManagement;